import React from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import useTable from 'src/hooks/useTable';
import { isEmpty } from 'lodash';

export type NotesDisplayColumn = {
  id: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  width?: string;
  render?: (value: any, row: any) => React.ReactNode;
};

export type NotesDisplayProps = {
  data: any[];
  columns: NotesDisplayColumn[];
  title?: string;
  titleButton?: string;
  onAddNote?: () => void;
  isAllowedToAdd?: boolean;
  emptyMessage?: string;
  maxRowsPerPage?: number;
};

export default function NotesDisplay({
  data = [],
  columns,
  title,
  titleButton,
  onAddNote,
  isAllowedToAdd = false,
  emptyMessage = 'No notes available',
  maxRowsPerPage = 10,
}: NotesDisplayProps) {
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: maxRowsPerPage,
  });

  const renderCellContent = (column: NotesDisplayColumn, value: any, row: any) => {
    // If custom render function provided, use it
    if (column.render) {
      return column.render(value, row);
    }

    // Default rendering based on column type
    switch (column.id) {
      case 'text':
      case 'description':
        return (
          <Tooltip title={value || ''}>
            <Box sx={{ maxWidth: 200 }}>
              <Typography
                style={{ cursor: 'pointer' }}
                noWrap
                sx={{ fontSize: `0.75rem` }}
              >
                {value || ''}
              </Typography>
            </Box>
          </Tooltip>
        );
      
      case 'topic':
        return (
          <Typography
            style={{ cursor: 'pointer' }}
            noWrap
            sx={{ fontSize: `0.75rem` }}
          >
            {value || 'General Note'}
          </Typography>
        );
      
      case 'author':
        return (
          <Typography
            style={{ cursor: 'pointer' }}
            noWrap
            sx={{ fontSize: `0.75rem` }}
          >
            {value || 'N/A'}
          </Typography>
        );
      
      case 'type':
        return (
          <Typography
            style={{ cursor: 'pointer' }}
            noWrap
            sx={{ fontSize: `0.75rem` }}
          >
            {value || 'N/A'}
          </Typography>
        );
      
      case 'date':
        return (
          <Typography
            style={{ cursor: 'pointer' }}
            noWrap
            sx={{ fontSize: `0.75rem` }}
          >
            {value || 'N/A'}
          </Typography>
        );
      
      case 'medium':
        return (
          <Typography
            style={{ cursor: 'pointer' }}
            noWrap
            sx={{ fontSize: `0.75rem` }}
          >
            {value || 'N/A'}
          </Typography>
        );
      
      default:
        return (
          <Typography
            style={{ cursor: 'pointer' }}
            noWrap
            sx={{ fontSize: `0.75rem` }}
          >
            {value || ''}
          </Typography>
        );
    }
  };

  return (
    <Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell 
                  key={`col-${index}`}
                  align={column.align || 'left'}
                  sx={{ width: column.width }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          
          {!isEmpty(data) ? (
            <TableBody>
              {data
                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: any, index: number) => (
                  <TableRow key={`row-${index}`}>
                    {columns.map((column, colIndex) => (
                      <TableCell
                        key={`cell-${index}-${colIndex}`}
                        align={column.align || 'left'}
                        sx={{ width: column.width }}
                      >
                        {renderCellContent(column, row[column.id], row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography variant="body2" align="center">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>
      
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data ? data.length : 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
}
